/* eslint-disable arrow-body-style */
/* eslint-disable no-nested-ternary */

import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Link, useParams } from 'react-router-dom';

import { downloadFile, getAllVersions, getReviseLogs } from 'apis/projectProposals.api';

import CompleteExplanation from 'features/ProjectProposal/components/CompleteExplanation';
import ViewDetailOwnerExplanation from 'features/ProjectProposal/components/ViewDetailOwnerExplanation';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';

import {
  checkOverdueDatetime,
  checkPermissionEditProjectProposal,
  formatFileName,
  getMaxDate,
  mapError,
  toCamelCaseString,
  is1593,
} from 'utils/func';

import { downloadPdfFile as downloadPdfPrequalificationReview } from 'apis/prequalificationReviews.api';
import { downloadPdfFile as downloadPdfSpecializeReview } from 'apis/specializeReviews.api';
import { downloadPdfFile as downloadPdfFinancialReview } from 'apis/financialReviews.api';
import { downloadPdfFile as downloadPdfMidtermReview } from 'apis/midtermReviews.api';
import { downloadPdfFile as downloadPdfAcceptanceReview } from 'apis/acceptanceReviews.api';
import { downloadPdfFile as downloadPdfFileDynamic } from 'apis/reviews.api';

import { toast } from 'layout';
import { COUNCIL_TYPE_CODE, TIMEZONE_VN, TOAST } from 'constant';
import Loading from 'components/Loading';
import RichTextViewer from 'components/RichTextViewer';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  getCouncilPrequalification,
  getCouncilSpecialization,
  getCouncilFinance,
  getCouncilMidterm,
  getCouncilAcceptance,
} from 'apis/councils.api';

const versionMap = {
  REQUEST_UPDATE: 1,
  REQUEST_EXPLANATION_PREQUALIFICATION: 2,
  REQUEST_EXPLANATION_SPECIALIZATION: 3,
  REQUEST_EXPLANATION_FINANCE: 4,
};

const councilTypeCodeMap = {
  REQUEST_EXPLANATION_PREQUALIFICATION: 'preliminarySelectionCouncil',
  REQUEST_EXPLANATION_SPECIALIZATION: 'specializationCouncil',
  REQUEST_EXPLANATION_FINANCE: 'financeCouncil',
  REQUEST_EXPLANATION_MIDTERM_REPORT: 'midtermReportCouncil',
  REQUEST_EXPLANATION_ACCEPTANCE_REPORT: 'acceptanceCouncil',
};

const reviseLogTypeCodesNotShowCouncilInfo = ['REQUEST_UPDATE'];

export default function ProjectProposalRequest() {
  // #region Data
  const { t } = useTranslation();
  const { id } = useParams();
  const dialogCompleteExplanationRef = useRef();
  const [selectedReviseLog, setSelectedReviseLog] = useState(null);

  const {
    data: requestsData,
    isLoading: isRequestsLoading,
    isError: isRequestsError,
    error: requestsError,
    refetch: refetchRequests,
  } = useQuery(
    ['projectProposalReviseLogs', id, 'ALL_OPEN'],
    () =>
      getReviseLogs({
        projectProposalId: id,
        types: [
          'REQUEST_UPDATE',
          'REQUEST_EXPLANATION_PREQUALIFICATION',
          'REQUEST_EXPLANATION_SPECIALIZATION',
          'REQUEST_EXPLANATION_FINANCE',
          'REQUEST_EXPLANATION_MIDTERM_REPORT',
          'REQUEST_EXPLANATION_ACCEPTANCE_REPORT',
          'REQUEST_EXPLANATION_LIQUIDATION',
        ],
        // isOpened: true,
      }),
    { enabled: !!id }
  );

  const { projectProposalReviseLogs, projectProposal, projectProposalCouncils } = useMemo(() => {
    if (requestsData?.data) {
      return {
        ...requestsData.data,
        projectProposalReviseLogs: requestsData.data?.projectProposalReviseLogs?.filter(
          (item) => item?.isOpened || item?.explanationTime || item?.type === 'REQUEST_UPDATE'
        ),
      };
    }
    return {};
  }, [requestsData]);

  const midtermCouncils = useMemo(() => {
    return (
      projectProposalCouncils?.filter(
        (council) => council?.councilType?.code === COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL
      ) || []
    );
  }, [projectProposalCouncils]);

  const latestMidtermCouncil = useMemo(() => {
    if (!midtermCouncils || midtermCouncils.length === 0) {
      return null;
    }

    return midtermCouncils.reduce((latest, current) => {
      const latestDate = new Date(latest.council.createdAt);
      const currentDate = new Date(current.council.createdAt);
      return currentDate > latestDate ? current : latest;
    });
  }, [midtermCouncils]);

  const {
    data: projectVersionsData,
    isLoading: isProjectVersionsLoading,
    isError: isProjectVersionsError,
    error: projectVersionsError,
    refetch: refetchProjectVersions,
  } = useQuery(['allProjectProposalVersions', id], () => getAllVersions(id), {
    enabled: !!id,
  });

  const projectVersions = useMemo(() => projectVersionsData?.data ?? [], [projectVersionsData]);

  const hasPrequalificationRequest = useMemo(() => {
    return projectProposalReviseLogs?.some(
      (log) => log.type === 'REQUEST_EXPLANATION_PREQUALIFICATION'
    );
  }, [projectProposalReviseLogs]);

  const hasSpecializationRequest = useMemo(() => {
    return projectProposalReviseLogs?.some(
      (log) => log.type === 'REQUEST_EXPLANATION_SPECIALIZATION'
    );
  }, [projectProposalReviseLogs]);

  const hasFinanceRequest = useMemo(() => {
    return projectProposalReviseLogs?.some((log) => log.type === 'REQUEST_EXPLANATION_FINANCE');
  }, [projectProposalReviseLogs]);

  const hasMidtermRequest = useMemo(() => {
    return projectProposalReviseLogs?.some(
      (log) => log.type === 'REQUEST_EXPLANATION_MIDTERM_REPORT'
    );
  }, [projectProposalReviseLogs]);

  const hasAcceptanceRequest = useMemo(() => {
    return projectProposalReviseLogs?.some(
      (log) => log.type === 'REQUEST_EXPLANATION_ACCEPTANCE_REPORT'
    );
  }, [projectProposalReviseLogs]);

  const { data: councilPrequalificationData } = useQuery(
    ['getCouncilPrequalification', projectProposal?.id],
    () => getCouncilPrequalification(projectProposal?.id),
    {
      enabled: !!projectProposal?.id && hasPrequalificationRequest,
    }
  );

  const { data: councilSpecializationData } = useQuery(
    ['getCouncilSpecialization', projectProposal?.id],
    () => getCouncilSpecialization(projectProposal?.id),
    {
      enabled: !!projectProposal?.id && hasSpecializationRequest,
    }
  );

  const { data: councilFinanceData } = useQuery(
    ['getCouncilFinance', projectProposal?.id],
    () => getCouncilFinance(projectProposal?.id),
    {
      enabled: !!projectProposal?.id && hasFinanceRequest,
    }
  );

  const { data: councilMidtermData } = useQuery(
    ['getCouncilMidterm', projectProposal?.id],
    () => getCouncilMidterm(projectProposal?.id, latestMidtermCouncil?.council?.summaryReport?.id),
    {
      enabled:
        !!projectProposal?.id &&
        !!latestMidtermCouncil?.council?.summaryReport?.id &&
        hasMidtermRequest,
    }
  );

  const { data: councilAcceptanceData } = useQuery(
    ['getCouncilAcceptance', projectProposal?.id],
    () => getCouncilAcceptance(projectProposal?.id),
    {
      enabled: !!projectProposal?.id && hasAcceptanceRequest,
    }
  );

  const { mutate: mutateDownloadFile } = useMutation(downloadFile);
  const { mutate: mutateDownloadPdfPrequalificationReview } = useMutation(
    downloadPdfPrequalificationReview
  );
  const { mutate: mutateDownloadPdfSpecializeReview } = useMutation(downloadPdfSpecializeReview);
  const { mutate: mutateDownloadPdfFinancialReview } = useMutation(downloadPdfFinancialReview);
  const { mutate: mutateDownloadPdfMidtermReview } = useMutation(downloadPdfMidtermReview);
  const { mutate: mutateDownloadPdfAcceptanceReview } = useMutation(downloadPdfAcceptanceReview);
  const { mutate: mutateDownloadPdfDynamic } = useMutation(downloadPdfFileDynamic);
  // #endregion Data

  // #region Event
  const handleDownloadFile = (path) => {
    mutateDownloadFile(
      {
        id,
        path,
      },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = formatFileName(path);

            link.click();
          }
        },
      }
    );
  };

  const handleDownloadCouncilMemberReview = (
    councilTypeCode,
    projectProposalId,
    councilId,
    councilMemberId,
    reviewerName
  ) => {
    const template = projectProposal?.template;

    if (is1593(template)) {
      let filename = '';
      if (councilTypeCode === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL) {
        filename = `Phieu nhan xet - danh gia so tuyen ${reviewerName}.pdf`;
      } else if (councilTypeCode === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) {
        filename = `Phieu nhan xet - danh gia tham dinh chuyen mon ${reviewerName}.pdf`;
      } else if (councilTypeCode === COUNCIL_TYPE_CODE.FINANCE_COUNCIL) {
        filename = `Phieu nhan xet - danh gia tham dinh kinh phi ${reviewerName}.pdf`;
      } else if (councilTypeCode === COUNCIL_TYPE_CODE.MIDTERM_COUNCIL) {
        filename = `Phieu nhan xet - danh gia giua ki ${reviewerName}.pdf`;
      } else if (councilTypeCode === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
        filename = `Phieu nhan xet - danh gia nghiem thu ${reviewerName}.pdf`;
      } else {
        filename = `Phieu nhan xet - danh gia ${reviewerName}.pdf`;
      }

      mutateDownloadPdfDynamic(
        {
          projectProposalId,
          councilId,
          councilMemberId,
          isReviewerHidden: true,
        },
        {
          onSuccess: (res) => {
            if (res?.data) {
              const file = new Blob([res.data]);
              const link = document.createElement('a');
              link.href = URL.createObjectURL(file);
              link.download = filename;
              link.click();
            }
          },
          onError: () => {
            toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
          },
        }
      );
    } else if (councilTypeCode === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL) {
      mutateDownloadPdfPrequalificationReview(
        {
          projectProposalId,
          councilId,
          councilMemberId,
        },
        {
          onSuccess: (res) => {
            if (res?.data) {
              const file = new Blob([res.data]);
              const filename = `Phieu nhan xet - danh gia so tuyen ${reviewerName}.pdf`;
              const link = document.createElement('a');
              link.href = URL.createObjectURL(file);
              link.download = filename;
              link.click();
            }
          },
          onError: () => {
            toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
          },
        }
      );
    } else if (councilTypeCode === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) {
      mutateDownloadPdfSpecializeReview(
        {
          projectProposalId,
          councilId,
          councilMemberId,
          isReviewerHidden: true,
        },
        {
          onSuccess: (res) => {
            if (res?.data) {
              const file = new Blob([res.data]);
              const filename = `Phieu nhan xet - danh gia tham dinh chuyen mon ${reviewerName}.pdf`;
              const link = document.createElement('a');
              link.href = URL.createObjectURL(file);
              link.download = filename;
              link.click();
            }
          },
          onError: () => {
            toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
          },
        }
      );
    } else if (councilTypeCode === COUNCIL_TYPE_CODE.FINANCE_COUNCIL) {
      mutateDownloadPdfFinancialReview(
        {
          projectProposalId,
          councilId,
          councilMemberId,
          isReviewerHidden: true,
        },
        {
          onSuccess: (res) => {
            if (res?.data) {
              const file = new Blob([res.data]);
              const filename = `Phieu nhan xet - danh gia tham dinh kinh phi ${reviewerName}.pdf`;
              const link = document.createElement('a');
              link.href = URL.createObjectURL(file);
              link.download = filename;
              link.click();
            }
          },
          onError: () => {
            toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
          },
        }
      );
    } else if (councilTypeCode === COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL) {
      mutateDownloadPdfMidtermReview(
        {
          projectProposalId,
          councilId,
          councilMemberId,
          isReviewerHidden: true,
        },
        {
          onSuccess: (res) => {
            if (res?.data) {
              const file = new Blob([res.data]);
              const filename = `Phieu nhan xet - danh gia giua ki ${reviewerName}.pdf`;
              const link = document.createElement('a');
              link.href = URL.createObjectURL(file);
              link.download = filename;
              link.click();
            }
          },
          onError: () => {
            toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
          },
        }
      );
    } else if (councilTypeCode === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
      mutateDownloadPdfAcceptanceReview(
        {
          projectProposalId,
          councilId,
          councilMemberId,
          isReviewerHidden: true,
        },
        {
          onSuccess: (res) => {
            if (res?.data) {
              const file = new Blob([res.data]);
              const filename = `Phieu nhan xet - danh gia nghiem thu ${reviewerName}.pdf`;
              const link = document.createElement('a');
              link.href = URL.createObjectURL(file);
              link.download = filename;
              link.click();
            }
          },
          onError: () => {
            toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
          },
        }
      );
    }
  };
  // #region Render
  const formatHeader = (value, rowIndex) => {
    const requestType = value?.type
      ? t(`projectProposal.viewHistory.status.${toCamelCaseString(value.type)}`)
      : null;

    const correspondingProjectVersion = projectVersions?.find(
      (item) => item?.version === versionMap[value?.type]
    );

    let deadline = value?.deadline;
    if (value?.type === 'REQUEST_UPDATE') {
      deadline = getMaxDate(
        correspondingProjectVersion?.organizationPrequalificationDeadline,
        correspondingProjectVersion?.program?.organizationPrequalificationDeadline
      );
    }
    if (value?.type === 'REQUEST_EXPLANATION_PREQUALIFICATION') {
      deadline = getMaxDate(
        value?.deadline,
        correspondingProjectVersion?.program?.staffPrequalificationDeadline
      );
    }

    // const lastFeedback = (value?.feedbacks ?? [])?.at(-1);

    let statusJsx = null;
    if (!value?.isOpened && (value?.explanationTime || value?.type === 'REQUEST_UPDATE')) {
      statusJsx = (
        <span className="font-medium text-green-500 text-base font-italic">
          {t('projectProposal.requestExplanation.ownerAddedExplanation')}
        </span>
      );
    } else if (value?.isOpened && checkOverdueDatetime(deadline)) {
      statusJsx = (
        <span className="font-medium text-red-500 text-base font-italic">
          {t('projectProposal.requestExplanation.overdueDeadline')}
        </span>
      );
    } else {
      statusJsx = (
        <span className="font-medium text-red-500 text-base font-italic">
          {t('projectProposal.requestExplanation.deadline')}:{' '}
          {deadline
            ? new Date(deadline).toLocaleString('en-GB', { timeZone: TIMEZONE_VN })
            : t('projectProposal.requestExplanation.noDeadline')}
        </span>
      );
    }

    return (
      <div className="flex flex-column text-lg font-bold gap-1">
        <span>
          {rowIndex + 1}. {requestType}
        </span>
        <div>
          {statusJsx}{' '}
          {!value?.isOpened && value?.explanationTime && (
            <span
              className="text-blue-500 underline font-italic font-normal text-base text-left"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setSelectedReviseLog(value);
              }}
            >
              ({t('projectProposal.requestExplanation.viewDetailOwnerExplanation')})
            </span>
          )}
        </div>
      </div>
    );
  };
  const formatConclusion = (value, ppCouncil) => (
    <div className="mb-2">
      <div className="font-semibold mb-2">
        {t(
          `council.view.${
            ppCouncil?.councilType?.code === COUNCIL_TYPE_CODE.FINANCE_COUNCIL
              ? 'financeCouncilResultAndInstructionToCompleteDocuments'
              : 'conclusion'
          }`
        )}
        :
      </div>
      {ppCouncil?.conclusion && (
        <RichTextViewer value={ppCouncil?.conclusion} showHeader={false} readOnly />
      )}
    </div>
  );

  const formatComment = (value) => (
    <div className="mb-2">
      <span className="font-semibold">{t('projectProposal.requestExplanation.comment')}:</span>
      <p className="my-2 text-justify" style={{ whiteSpace: 'pre-wrap' }}>
        {value?.comment}
      </p>
    </div>
  );

  const formatCommentFileUrls = (value) => (
    <div className="mb-2">
      <span className="mr-2 font-semibold">
        {t('projectProposal.requestExplanation.commentFile')}:
      </span>
      {value?.commentFileUrls?.length > 0 && (
        <ul className="list-disc">
          {value?.commentFileUrls?.map((item, index) => (
            <li className="-ml-4" key={item?.id ?? index}>
              <span
                className="p-button p-button-text p-button-sm p-0"
                onClick={() => handleDownloadFile(item?.name)}
              >
                <span className="text-blue-500 underline font-italic text-base">
                  {formatFileName(item?.name)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const formatFeedbacks = (value) => {
    const newFeedbacks = (value?.feedbacks ?? [])?.filter(
      (item) => new Date(item?.createdAt) > new Date(value?.explanationTime)
    );

    const newFeedbacksJSX =
      newFeedbacks?.length > 1 ? (
        <ul className="list-disc">
          {newFeedbacks?.map((item, index) => (
            <li key={item?.id ?? index} className="-ml-4" style={{ whiteSpace: 'pre-wrap' }}>
              <span>{item?.content}</span>
            </li>
          ))}
        </ul>
      ) : newFeedbacks?.length === 1 ? (
        <p className="my-2 text-justify" style={{ whiteSpace: 'pre-wrap' }}>
          {newFeedbacks?.[0]?.content}
        </p>
      ) : null;

    return (
      <div className="mb-2">
        <span className="mr-2 font-semibold">
          {t('projectProposal.requestExplanation.feedback')}:
        </span>
        {newFeedbacksJSX}
      </div>
    );
  };

  const formatReportFile = (value, ppCouncil) => {
    const reportFile = ppCouncil?.reportFileUrl;
    const isShowReport = ppCouncil?.council?.isShowReport;

    // Chỉ hiển thị nếu isShowReport là true
    if (!isShowReport) {
      return null;
    }

    return (
      <div className="mb-2">
        <span className="mr-2 font-semibold">
          {t('projectProposal.requestExplanation.reportFile')}:
        </span>
        {reportFile && (
          <div>
            <span
              className="p-button p-button-text p-button-sm px-0 text-blue-500 underline font-italic text-base text-left"
              onClick={() => handleDownloadFile(reportFile)}
            >
              {formatFileName(reportFile)}
            </span>
          </div>
        )}
      </div>
    );
  };

  // eslint-disable-next-line no-shadow
  const formatCouncilReview = (rowData, ppCouncil) => {
    // Xác định data council dựa trên councilTypeCode
    let councilData = null;
    const councilTypeCode = ppCouncil?.councilType?.code;

    if (councilTypeCode === COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL) {
      councilData = councilPrequalificationData?.data;
    } else if (councilTypeCode === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) {
      councilData = councilSpecializationData?.data;
    } else if (councilTypeCode === COUNCIL_TYPE_CODE.FINANCE_COUNCIL) {
      councilData = councilFinanceData?.data;
    } else if (councilTypeCode === COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL) {
      councilData = councilMidtermData?.data;
    } else if (councilTypeCode === COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL) {
      councilData = councilAcceptanceData?.data;
    }

    const actionBodyTemplate = (data) => {
      if (!data?.councilMemberRole?.isAllowedReview) {
        return null; // Không hiển thị nút tải xuống nếu không được phép review
      }

      return (
        <div className="flex justify-content-center">
          <Button
            icon="pi pi-download text-2xl"
            rounded
            text
            severity="info"
            tooltip={t('projectProposal.requestExplanation.downloadReviewFile')}
            tooltipOptions={{ position: 'left' }}
            onClick={() => {
              handleDownloadCouncilMemberReview(
                councilTypeCode,
                ppCouncil?.projectProposalId,
                councilData?.councilId,
                data?.projectProposalCouncilMembers[0]?.councilMemberId,
                data?.contact?.fullname
              );
            }}
          />
        </div>
      );
    };

    const formatRole = (value) => {
      return value?.councilMemberRole?.code && t(`council.role.${value?.councilMemberRole?.code}`);
    };

    const formatOrganization = (member) => {
      return t('lang') === 'vi'
        ? member?.contact?.organization?.name
        : member?.contact?.organization?.englishName;
    };

    return (
      <div className="mb-3">
        <span className="mr-2 font-semibold">
          {t('projectProposal.requestExplanation.memberReview')}:
        </span>
        {councilData?.councilMembers?.length > 0 ? (
          <div className="mt-2">
            <DataTable
              value={councilData?.councilMembers}
              className="p-datatable-gridlines header-table"
              stripedRows
              showGridlines
            >
              <Column
                header={t('projectProposal.requestExplanation.no')}
                body={(data, options) => options.rowIndex + 1}
                style={{ width: '4rem', textAlign: 'center' }}
              />
              <Column
                field="contact.fullname"
                header={t('projectProposal.requestExplanation.fullname')}
                style={{ minWidth: '6rem' }}
              />
              <Column
                field="contact.email"
                header={t('projectProposal.requestExplanation.email')}
                style={{ minWidth: '6rem' }}
              />
              <Column
                header={t('projectProposal.requestExplanation.role')}
                style={{ minWidth: '6rem' }}
                body={formatRole}
              />
              <Column
                header={t('projectProposal.requestExplanation.organization')}
                style={{ minWidth: '6rem' }}
                body={formatOrganization}
              />
              <Column
                body={actionBodyTemplate}
                header={t('projectProposal.requestExplanation.actions')}
                style={{ width: '6rem', textAlign: 'center' }}
              />
            </DataTable>
          </div>
        ) : (
          <p className="text-gray-500 mt-2"> </p>
        )}
      </div>
    );
  };

  const formatActions = (value, ppCouncil) => {
    const correspondingProjectVersion = projectVersions?.find(
      (item) => item?.version === versionMap[value?.type]
    );

    let deadline = value?.deadline;
    if (value?.type === 'REQUEST_UPDATE') {
      deadline = getMaxDate(
        correspondingProjectVersion?.organizationPrequalificationDeadline,
        correspondingProjectVersion?.program?.organizationPrequalificationDeadline
      );
    }
    if (value?.type === 'REQUEST_EXPLANATION_PREQUALIFICATION') {
      deadline = getMaxDate(
        value?.deadline,
        correspondingProjectVersion?.program?.staffPrequalificationDeadline
      );
    }

    const isEditableProjectProposal = checkPermissionEditProjectProposal(
      correspondingProjectVersion,
      value
    );

    return (
      <div className="flex justify-content-end gap-3">
        {/* eslint-disable indent */}
        {isEditableProjectProposal && (
          <div className="border-round-lg" style={{ backgroundColor: '#F59E0B' }}>
            <Link
              className="edit-project-proposal flex align-items-center justify-content-center font-bold text-white border-round m-2 h-2rem"
              to={`/project-proposal/edit/${correspondingProjectVersion?.id}?reviseLogId=${value?.id}&template=${correspondingProjectVersion?.program?.template}&programId=${correspondingProjectVersion?.program?.id}`}
            >
              {t(
                `projectProposal.completeExplanationRequest.${
                  ppCouncil?.councilType?.code === COUNCIL_TYPE_CODE.FINANCE_COUNCIL
                    ? 'completeDocument'
                    : 'editProjectProposal'
                }`
              )}
            </Link>
          </div>
        )}

        {value?.type !== 'REQUEST_UPDATE' &&
          value?.isOpened &&
          !checkOverdueDatetime(deadline) &&
          (isEditableProjectProposal ? (
            <Button
              className="font-bold w-auto cursor-auto"
              severity="secondary"
              tooltip={t('projectProposal.completeExplanationRequest.explainTooltip')}
              tooltipOptions={{ position: 'left' }}
            >
              {t('projectProposal.completeExplanationRequest.explain')}
            </Button>
          ) : (
            <Button
              onClick={() => {
                dialogCompleteExplanationRef.current.open({ value });
              }}
              className="font-bold w-auto"
              tooltip={
                isEditableProjectProposal &&
                t('projectProposal.completeExplanationRequest.explainTooltip')
              }
              tooltipOptions={{ position: 'top' }}
            >
              {t('projectProposal.completeExplanationRequest.explain')}
            </Button>
          ))}
      </div>
    );
  };

  // #endregion Render

  if (isRequestsError) {
    return <h4 className="text-red-500">{mapError(t, requestsError)}</h4>;
  }
  if (isProjectVersionsError) {
    return <h4 className="text-red-500">{mapError(t, projectVersionsError)}</h4>;
  }

  return (
    <div className="grid">
      {(isProjectVersionsLoading || isRequestsLoading) && <Loading />}

      <div className="col-12">
        <div className="card">
          <div className="flex justify-content-between align-items-end mb-1 pb-2">
            <h5 className="p-0 m-0">
              {t('projectProposal.requestExplanation.updateInfoRequests')}
            </h5>
          </div>
          <div className="mb-2">
            <span className="p-0 m-0 text-blue-500 font-bold text-lg">
              {t('projectProposal.label')}:{' '}
            </span>
            <span className="text-blue-500 font-bold text-lg">
              {projectProposal?.vietnameseName}
            </span>
          </div>
          <div className="my-4 border-double p-2">
            <p className="my-1">
              <b>{t('explanationGuideline.title')}:</b>
            </p>
            <p>
              <b className="underline">{t('guideline.step')} 1:</b>{' '}
              {t('explanationGuideline.step1.1')}
            </p>
            <p>
              <b className="underline">{t('guideline.step')} 2:</b>{' '}
              {t('explanationGuideline.step2.1')}{' '}
              <b className="text-orange-400">{t('explanationGuideline.step2.2')}</b>{' '}
              {t('explanationGuideline.step2.3')}{' '}
              <b>&quot;{t('explanationGuideline.step2.4')}&quot;</b>{' '}
              {t('explanationGuideline.step2.5')}
              <b> &quot;{t('explanationGuideline.step2.6')}&quot;</b>.
              <p className="my-1">
                <i>
                  {t('explanationGuideline.step2.7')}{' '}
                  <b className="text-orange-400">{t('explanationGuideline.step2.8')}</b>
                  {t('explanationGuideline.step2.9')}.
                </i>
              </p>
            </p>
            <p>
              <b className="underline">{t('guideline.step')} 3:</b>{' '}
              {t('explanationGuideline.step3.1')}{' '}
              <b className="text-indigo-500">{t('explanationGuideline.step3.2')}</b>{' '}
              {t('explanationGuideline.step3.3')}.
            </p>
          </div>

          {projectProposalReviseLogs?.length > 0 && (
            <Accordion
              multiple
              className="explanation-request-list"
              activeIndex={projectProposalReviseLogs
                .map((item, index) => (item?.isOpened ? index : null))
                .filter((index) => index !== null)}
            >
              {projectProposalReviseLogs?.map((rowData, rowIndex) => {
                const correspondingPpCouncil = projectProposalCouncils?.find(
                  (ppCouncil) =>
                    ppCouncil?.councilType?.code === councilTypeCodeMap?.[rowData?.type]
                );
                const councilTypeCode = correspondingPpCouncil?.councilType?.code;

                return (
                  <AccordionTab key={rowData.id} header={formatHeader(rowData, rowIndex)}>
                    {/* Kết luận và kiến nghị của Hội đồng */}
                    {reviseLogTypeCodesNotShowCouncilInfo.includes(rowData?.type) === false &&
                      formatConclusion(rowData, correspondingPpCouncil)}
                    {/* Ghi chú */}
                    {formatComment(rowData)}
                    {/* Các file đính kèm */}
                    {formatCommentFileUrls(rowData)}
                    {/* Nhận xét từ chuyên viên */}
                    {reviseLogTypeCodesNotShowCouncilInfo.includes(rowData?.type) === false &&
                      formatFeedbacks(rowData)}
                    {/* Biên bản họp hội đồng */}
                    {reviseLogTypeCodesNotShowCouncilInfo.includes(rowData?.type) === false &&
                      councilTypeCode !== COUNCIL_TYPE_CODE.FINANCE_COUNCIL &&
                      formatReportFile(rowData, correspondingPpCouncil)}
                    {/* Nhận xét của thành viên */}
                    {reviseLogTypeCodesNotShowCouncilInfo.includes(rowData?.type) === false &&
                      formatCouncilReview(rowData, correspondingPpCouncil)}

                    {formatActions(rowData, correspondingPpCouncil)}
                  </AccordionTab>
                );
              })}
            </Accordion>
          )}

          <CompleteExplanation
            ref={dialogCompleteExplanationRef}
            refetchRequests={refetchRequests}
            refetchProjectVersions={refetchProjectVersions}
          />
          <ViewDetailOwnerExplanation
            reviseLog={selectedReviseLog}
            handleClose={() => setSelectedReviseLog(null)}
            handleDownloadFile={handleDownloadFile}
          />
        </div>
      </div>
    </div>
  );
}
